import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as ErrorIcon,
  Science as TestIcon,
} from '@mui/icons-material';
import metaService, {
  CapiConfig,
  CapiStats,
} from '../../services/marketing-meta.service';

interface Props {
  onNotify: (message: string, severity?: 'success' | 'error') => void;
}

/**
 * Conversions API — configuração e saúde.
 *
 * A CAPI é o caminho de servidor dos eventos: o browser perde 20–40% para
 * bloqueadores e para o iOS, o servidor não perde nada. Enquanto o Pixel ID e
 * o token não estiverem preenchidos, o código corre e não envia nada — que é
 * o comportamento seguro, mas também significa zero sinal para as campanhas.
 */
export default function CapiPanel({ onNotify }: Props) {
  const [config, setConfig] = useState<CapiConfig | null>(null);
  const [stats, setStats] = useState<CapiStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [pixelId, setPixelId] = useState('');
  const [token, setToken] = useState('');
  const [testCode, setTestCode] = useState('');
  const [enabled, setEnabled] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await metaService.getCapiConfig();
      setConfig(cfg);
      setPixelId(cfg.pixel_id ?? '');
      setTestCode(cfg.test_event_code ?? '');
      setEnabled(cfg.enabled);
      // As estatísticas dependem da tabela de log, que pode ainda não existir.
      try {
        setStats(await metaService.getCapiStats(7));
      } catch {
        setStats(null);
      }
    } catch (e: any) {
      onNotify(e?.response?.data?.error || 'Erro ao carregar configuração CAPI', 'error');
    } finally {
      setLoading(false);
    }
  }, [onNotify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await metaService.setupCapi();
      const payload: Record<string, string> = {
        pixel_id: pixelId.trim(),
        capi_enabled: enabled ? '1' : '0',
        capi_test_event_code: testCode.trim(),
      };
      // Campo vazio = não mexer no token guardado. Assim o formulário pode ser
      // reguardado sem apagar um token que já lá está.
      if (token.trim()) payload.capi_access_token = token.trim();

      await metaService.saveCapiConfig(payload);
      setToken('');
      onNotify('Configuração CAPI guardada');
      load();
    } catch (e: any) {
      onNotify(e?.response?.data?.error || 'Erro ao guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await metaService.testCapi();
      if (r.ok) {
        onNotify(
          `Evento aceite pelo Meta${r.eventsReceived ? ` (${r.eventsReceived} recebido)` : ''}. Vê em Events Manager → Test Events.`,
        );
      } else if (r.skipped === 'unconfigured') {
        onNotify('Falta o Pixel ID ou o token de acesso.', 'error');
      } else if (r.skipped === 'disabled') {
        onNotify('A CAPI está desligada.', 'error');
      } else {
        onNotify(r.error || 'O Meta rejeitou o evento.', 'error');
      }
      load();
    } catch (e: any) {
      onNotify(e?.response?.data?.error || 'Erro no teste', 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const configured = !!config?.pixel_id && !!config?.has_access_token;

  return (
    <Stack spacing={2}>
      {!configured && (
        <Alert severity="warning">
          A Conversions API ainda não está configurada — nenhum evento de servidor
          está a chegar ao Meta. O token gera-se em{' '}
          <Link
            href="https://business.facebook.com/events_manager2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Events Manager
          </Link>{' '}
          → Settings → Conversions API → Generate access token.
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Configuração
        </Typography>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Pixel ID"
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              helperText="O mesmo que está no site. Ex.: 27320972990868937"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label="Access token da CAPI"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={config?.has_access_token ? '•••••••• (guardado)' : ''}
              helperText={
                config?.has_access_token
                  ? 'Deixa vazio para manter o token actual'
                  : 'Token de sistema com permissão sobre o pixel'
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Test event code"
              value={testCode}
              onChange={(e) => setTestCode(e.target.value)}
              helperText="Só durante a validação — apagar antes de produção, senão os eventos ficam de fora dos relatórios"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Stack spacing={1} sx={{ pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                }
                label="CAPI activa"
              />
              <Typography variant="caption" color="text.secondary">
                Versão da Graph API: {config?.graph_version ?? '—'}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'A guardar…' : 'Guardar'}
          </Button>
          <Tooltip title="Envia um PageView de teste e mostra a resposta do Meta">
            <span>
              <Button
                variant="outlined"
                startIcon={<TestIcon />}
                onClick={handleTest}
                disabled={testing || !configured}
              >
                {testing ? 'A testar…' : 'Testar'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Saúde dos envios · últimos {stats?.dias ?? 7} dias
        </Typography>

        {!stats || stats.por_evento.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Ainda não há envios registados.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Evento</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Sucesso</TableCell>
                  <TableCell>Último</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.por_evento.map((r) => (
                  <TableRow key={`${r.event_name}-${r.action_source}`}>
                    <TableCell>{r.event_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={r.action_source === 'website' ? 'browser' : 'servidor'}
                      />
                    </TableCell>
                    <TableCell align="right">{r.total.toLocaleString('pt-CV')}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        icon={r.taxa_sucesso === 100 ? <OkIcon /> : <ErrorIcon />}
                        color={
                          r.taxa_sucesso === 100
                            ? 'success'
                            : r.taxa_sucesso >= 90
                              ? 'warning'
                              : 'error'
                        }
                        label={`${r.taxa_sucesso}%`}
                      />
                    </TableCell>
                    <TableCell>
                      {r.ultimo ? new Date(r.ultimo).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {stats && stats.erros_frequentes.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Erros mais frequentes
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Histórico dos últimos {stats.dias} dias. Se a última ocorrência for
              anterior à tua correcção, já está resolvido — o registo fica para
              consulta.
            </Typography>
            <Stack spacing={1}>
              {stats.erros_frequentes.map((e, i) => {
                // Um erro que não se repete há mais de uma hora provavelmente
                // já foi corrigido; mostrá-lo a vermelho vivo assusta à toa.
                const ultimo = e.ultimo ? new Date(e.ultimo) : null;
                const recente =
                  !!ultimo && Date.now() - ultimo.getTime() < 60 * 60 * 1000;
                return (
                  <Alert key={i} severity={recente ? 'error' : 'warning'} variant="outlined">
                    <strong>{e.event_name}</strong> · {e.n}× — {e.error_message}
                    {ultimo && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {recente ? 'Última ocorrência: ' : 'Sem repetir desde: '}
                        {ultimo.toLocaleString('pt-CV')}
                      </Typography>
                    )}
                  </Alert>
                );
              })}
            </Stack>
          </>
        )}
      </Paper>
    </Stack>
  );
}
